/**
 * @description Lightweight fetch wrapper replacing axios with native fetch API.
 * @date        2026-05-18
 */

const JSON_CONTENT_TYPE = 'application/json';
const ABSOLUTE_URL_PATTERN = /^[a-z][a-z\d+.-]*:/i;

export class HttpError extends Error {
  /**
   * @description Create an HTTP error with the response metadata and parsed response body.
   * @author      Arya Ingale
   * @date        2026-05-18
   */
  constructor(message, response, data) {
    super(message);
    this.name = 'HttpError';
    this.status = response.status;
    this.statusText = response.statusText;
    this.headers = response.headers;
    this.data = data;
  }
}

/**
 * @description Returns true when the value should be serialized as JSON by this wrapper.
 * @date        2026-05-18
 */
const shouldSerializeJson = (body) => {
  if (body === undefined || body === null || typeof body === 'string') {
    return false;
  }

  if (typeof FormData !== 'undefined' && body instanceof FormData) {
    return false;
  }

  if (typeof Blob !== 'undefined' && body instanceof Blob) {
    return false;
  }

  if (typeof URLSearchParams !== 'undefined' && body instanceof URLSearchParams) {
    return false;
  }

  if (typeof ArrayBuffer !== 'undefined' && body instanceof ArrayBuffer) {
    return false;
  }

  if (ArrayBuffer.isView(body)) {
    return false;
  }

  if (typeof body.pipe === 'function') {
    return false;
  }

  return true;
};

/**
 * @description Append query params to a relative URL without requiring a base URL.
 * @date        2026-05-18
 */
const appendParamsToRelativeUrl = (url, params) => {
  const hashIndex = url.indexOf('#');
  const hash = hashIndex === -1 ? '' : url.slice(hashIndex);
  const urlWithoutHash = hashIndex === -1 ? url : url.slice(0, hashIndex);
  const queryIndex = urlWithoutHash.indexOf('?');
  const path = queryIndex === -1 ? urlWithoutHash : urlWithoutHash.slice(0, queryIndex);
  const query = queryIndex === -1 ? '' : urlWithoutHash.slice(queryIndex + 1);
  const searchParams = new URLSearchParams(query);

  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null) {
      continue;
    }

    if (Array.isArray(value)) {
      searchParams.delete(key);
      for (const item of value) {
        searchParams.append(key, String(item));
      }
      continue;
    }

    searchParams.set(key, String(value));
  }

  const queryString = searchParams.toString();
  return `${path}${queryString ? `?${queryString}` : ''}${hash}`;
};

/**
 * @description Build a request URL with query params for native fetch.
 * @date        2026-05-18
 */
const buildUrl = (url, baseUrl, params) => {
  const browserBaseUrl = typeof window !== 'undefined' ? window.location.href : undefined;
  const resolvedBaseUrl = baseUrl || browserBaseUrl;
  const shouldUseUrlApi = resolvedBaseUrl || ABSOLUTE_URL_PATTERN.test(url);

  if (!shouldUseUrlApi) {
    return params ? appendParamsToRelativeUrl(url, params) : url;
  }

  const requestUrl = resolvedBaseUrl ? new URL(url, resolvedBaseUrl) : new URL(url);

  if (!params) {
    return requestUrl.toString();
  }

  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null) {
      continue;
    }

    if (Array.isArray(value)) {
      requestUrl.searchParams.delete(key);
      for (const item of value) {
        requestUrl.searchParams.append(key, String(item));
      }
      continue;
    }

    requestUrl.searchParams.set(key, String(value));
  }

  return requestUrl.toString();
};

/**
 * @description Combines timeout cancellation with an optional caller-provided abort signal.
 * @date        2026-05-18
 */
const createRequestSignal = (timeout, signal) => {
  const controller = new AbortController();
  const timeoutId = timeout > 0 ? setTimeout(() => controller.abort(), timeout) : undefined;

  if (!signal) {
    return { signal: controller.signal, timeoutId, cleanup: undefined };
  }

  if (signal.aborted) {
    controller.abort();
    return { signal: controller.signal, timeoutId, cleanup: undefined };
  }

  const abortRequest = () => controller.abort();
  signal.addEventListener('abort', abortRequest, { once: true });

  return {
    signal: controller.signal,
    timeoutId,
    cleanup: () => signal.removeEventListener('abort', abortRequest),
  };
};

/**
 * @description Parses fetch response body safely for JSON, text, empty, and binary responses.
 * @date        2026-05-18
 */
const parseResponseBody = async (response, responseType) => {
  if (response.status === 204 || response.status === 205) {
    return undefined;
  }

  if (responseType === 'arrayBuffer' || responseType === 'arraybuffer') {
    return await response.arrayBuffer();
  }

  if (responseType === 'blob') {
    return await response.blob();
  }

  if (responseType === 'text') {
    return await response.text();
  }

  const text = await response.text();

  if (!text) {
    return undefined;
  }

  const contentType = response.headers.get('content-type') || '';
  const isJson = contentType.includes(JSON_CONTENT_TYPE) || contentType.includes('+json');

  if (responseType === 'json') {
    return JSON.parse(text);
  }

  if (isJson) {
    try {
      return JSON.parse(text);
    } catch (error) {
      return text;
    }
  }

  return text;
};

/**
 * @description Run an HTTP request with native fetch and axios-like response/error handling.
 * @date        2026-05-18
 */
export const request = async (url, options = {}) => {
  const {
    timeout = 60000,
    params,
    baseUrl,
    responseType = 'auto',
    validateStatus = (status) => status >= 200 && status < 300,
    ...fetchOptions
  } = options;
  const requestUrl = buildUrl(url, baseUrl, params);
  const headers = new Headers(fetchOptions.headers);
  let body = fetchOptions.body;
  const requestSignal = createRequestSignal(timeout, fetchOptions.signal);

  if (shouldSerializeJson(body)) {
    body = JSON.stringify(body);
    if (!headers.has('Content-Type')) {
      headers.set('Content-Type', JSON_CONTENT_TYPE);
    }
  }

  try {
    const response = await fetch(requestUrl, {
      ...fetchOptions,
      body,
      headers,
      signal: requestSignal.signal,
    });
    const data = await parseResponseBody(response, responseType);

    if (!validateStatus(response.status)) {
      throw new HttpError(`HTTP ${response.status}: ${response.statusText}`, response, data);
    }

    return {
      data,
      status: response.status,
      statusText: response.statusText,
      headers: response.headers,
      ok: response.ok,
      url: response.url,
    };
  } finally {
    clearTimeout(requestSignal.timeoutId);
    if (requestSignal.cleanup) {
      requestSignal.cleanup();
    }
  }
};

/**
 * @description Create an HTTP client with defaults shared by every request.
 * @date        2026-05-18
 */
export const createHttpClient = (defaults = {}) => {
  const mergeOptions = (options = {}) => {
    const headers = new Headers(defaults.headers);
    const optionHeaders = new Headers(options.headers);

    for (const [key, value] of optionHeaders.entries()) {
      headers.set(key, value);
    }

    return {
      ...defaults,
      ...options,
      headers,
      params: {
        ...(defaults.params || {}),
        ...(options.params || {}),
      },
    };
  };

  return {
    request: (url, options) => request(url, mergeOptions(options)),
    get: (url, options) => request(url, mergeOptions({ ...options, method: 'GET' })),
    delete: (url, options) => request(url, mergeOptions({ ...options, method: 'DELETE' })),
    post: (url, body, options) => request(url, mergeOptions({ ...options, method: 'POST', body })),
    put: (url, body, options) => request(url, mergeOptions({ ...options, method: 'PUT', body })),
    patch: (url, body, options) => request(url, mergeOptions({ ...options, method: 'PATCH', body })),
  };
};

export const http = createHttpClient();

export default http;
